// Configuration for the Tarot frontend application
import {
  getCurrencyConfig,
  formatCurrencyFromCents as formatCurrencyUtil,
} from "./currency";

function parsePublicStringMap(value?: string): Record<string, string> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed)
        .filter((entry): entry is [string, string] => typeof entry[1] === "string")
        .map(([key, item]) => [key, item.trim()])
        .filter(([, item]) => Boolean(item)),
    );
  } catch {
    return {};
  }
}

export const config = {
  // API Configuration
  api: {
    baseUrl: process.env.NEXT_PUBLIC_API_URL || "",
    timeout: 10000, // 10 seconds
  },

  // Payments configuration
  payments: {
    // Payment provider selected for this storefront.
    provider:
      (process.env.NEXT_PUBLIC_PAYMENT_PROVIDER as
        | "mercadopago"
        | "stripe"
        | "hotmart") ||
      "mercadopago",
    // Mercado Pago public key for frontend SDK
    mpPublicKey: process.env.NEXT_PUBLIC_MP_PUBLIC_KEY || "",
    // Locale for Mercado Pago Bricks (e.g., 'pt-BR')
    mpLocale: process.env.NEXT_PUBLIC_MP_LOCALE || "pt-BR",
    // Stripe publishable key for frontend SDK
    stripePublicKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "",
  },

  // Internationalization
  i18n: {
    // Mercado/variante da landing: 'pt-BR' (default), 'en-US' ou 'pt-PT'
    language: process.env.NEXT_PUBLIC_LANGUAGE || "pt-BR",
  },

  // Auth configuration
  // IMPORTANT: Do NOT put credentials in NEXT_PUBLIC variables.
  // Server-side routes will read private envs (e.g., API_AUTH_USERNAME, API_AUTH_PASSWORD)
  // and manage an HttpOnly cookie with the API token. Frontend should never see credentials.
  auth: {},

  // Application settings
  app: {
    name: "Carta Canalizada",
    description: "Cartas Canalizadas Especiais",
    version: "1.0.0",
  },

  // Public support channel shown in landing pages and customer-facing UI.
  support: {
    channel: ["whatsapp", "whattsapp"].includes(
      (process.env.NEXT_PUBLIC_SUPPORT_CHANNEL || "instagram").toLowerCase(),
    )
      ? "whatsapp"
      : "instagram",
    instagramHandle:
      process.env.NEXT_PUBLIC_SUPPORT_INSTAGRAM_HANDLE ||
      "@cartacanalizada.oficial",
    whatsappDisplay:
      process.env.NEXT_PUBLIC_SUPPORT_WHATSAPP_DISPLAY || "+55 11 97336-1085",
    whatsappUrl:
      process.env.NEXT_PUBLIC_SUPPORT_WHATSAPP_URL ||
      "https://wa.me/5511973361085",
  },

  // Form settings
  form: {
    autoSaveDelay: 2000, // 2 seconds
    maxSteps: 7,
  },

  // Pricing configuration (now fetched from backend API)
  // See: src/services/pricing.ts for dynamic pricing
  pricing: {
    // Fallback values used only if API is unavailable
    fallback: (() => {
      const currencyConfig = getCurrencyConfig();
      const fallbackPriceByCurrency: Record<string, number> = {
        USD: 3300,
        EUR: 1990,
        BRL: 2590,
      };
      const fallbackPrice = fallbackPriceByCurrency[currencyConfig.code] ?? 2590;
      const descriptionByCurrency: Record<string, string> = {
        USD: "Channeled Letter: When Tarot translates the voice of the one you love.",
        EUR: "Carta Canalizada: quando o Tarot traduz a voz de quem tu amas.",
        BRL: "Carta Canalizada: Quando o Tarot traduz a voz de quem você ama.",
      };
      return {
        priceCents: fallbackPrice,
        priceDisplay: formatCurrencyUtil(fallbackPrice),
        currency: currencyConfig.code,
        description:
          descriptionByCurrency[currencyConfig.code] ??
          descriptionByCurrency.BRL,
      };
    })(),
    // Optional coupon configuration (UI only; backend validates and applies discount)
    coupon: {
      name: process.env.NEXT_PUBLIC_COUPON_NAME || "", // exact code shown/accepted (case-insensitive)
      percent: Number(process.env.NEXT_PUBLIC_COUPON_PERCENT || 0), // e.g., 10 => 10%
    },
  },

  // UI settings
  ui: {
    animationDuration: 300,
    // Controls visibility of the request summary block in payment step (Step 8)
    showPaymentRequestSummary:
      (
        process.env.NEXT_PUBLIC_SHOW_PAYMENT_REQUEST_SUMMARY || "false"
      ).toLowerCase() === "true",
    theme: {
      colors: {
        primary: "#A23427", // Vermelho Carmim
        secondary: "#D5A253", // Dourado Queimado
        background: "#F3E3C2", // Creme Claro
        accent: "#763A2B", // Marrom Avermelhado
      },
    },
  },

  // Analytics settings
  analytics: {
    // Controls whether Meta Pixel events (fbq) are sent from the browser.
    // This does NOT affect Conversions API events sent to the backend.
    pixelEnabled:
      (
        process.env.NEXT_PUBLIC_ANALYTICS_PIXEL_ENABLED || "false"
      ).toLowerCase() === "true",
    // Controls whether events are sent to our backend (CAPI).
    capiEnabled:
      (
        process.env.NEXT_PUBLIC_ANALYTICS_CAPI_ENABLED || "false"
      ).toLowerCase() === "true",
    // TikTok is intentionally independent from Meta and off by default.
    tiktokPixelEnabled:
      (
        process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ENABLED || "false"
      ).toLowerCase() === "true",
    tiktokEventsApiEnabled:
      (
        process.env.NEXT_PUBLIC_TIKTOK_EVENTS_API_ENABLED || "false"
      ).toLowerCase() === "true",
    tiktokPixelId: process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID || "",
    tiktokPixelIds: parsePublicStringMap(
      process.env.NEXT_PUBLIC_TIKTOK_PIXEL_IDS,
    ),
  },
};

/**
 * Idioma enviado à API. O backend tem configuração por idioma (preço, cupom,
 * e-mail, pixels, status page), então pt-PT vai como pt-PT — valores
 * desconhecidos caem em pt-BR, que é o default do backend.
 */
export function getApiLanguage(): "pt-BR" | "en-US" | "pt-PT" {
  const language = config.i18n.language;
  if (language === "en-US") return "en-US";
  if (language === "pt-PT") return "pt-PT";
  return "pt-BR";
}

/** Mercado de língua portuguesa (Brasil ou Portugal). */
export function isPortugueseLanguage(): boolean {
  return getApiLanguage() !== "en-US";
}

/**
 * País usado como padrão para máscara, DDI e validação de telefone/WhatsApp
 * no mercado atual.
 */
export function getMarketPhoneCountry(): "BR" | "PT" | "US" {
  if (config.i18n.language === "pt-PT") return "PT";
  if (config.i18n.language === "en-US") return "US";
  return "BR";
}

// Função auxiliar para formatar preço em centavos usando configuração de moeda atual
export const formatPrice = (cents: number): string => {
  return formatCurrencyUtil(cents);
};

export default config;
